import { useMediaQuery } from '@vueuse/core';
import type { ComputedRef } from 'vue';

// Résolveur de format du Design System v2 : quel arbre de composants monter selon la LARGEUR DE
// VIEWPORT. Port de `ui_kits/goldorac/mobile/Shell.jsx` (Claude Design 4da5d5a8), qui dérive le
// breakpoint Tailwind effectif de `window.innerWidth` :
//
//   sm 640 / md 768   → arbre mobile (shell téléphone)
//   lg 1024           → app desktop, la plus compacte (fenêtre étroite)
//   xl 1280           → app desktop, compacte
//   2xl 1536 et plus  → app desktop, confort complet
//
// Donc mobile = viewport < 1024, SANS critère User-Agent. Les seuils existent aussi comme tokens
// (`--bp-*` dans assets/styles/ds/tokens.css) mais y sont documentés « références seules,
// inutilisables dans un @media » : la bascule est en JS, ici.
//
// À NE PAS CONFONDRE avec `store.isMobile` (app.vue), qui reste sur son critère User-Agent
// historique et pilote une quarantaine d'affichages legacy (Documents, Messages, menus…).
// Élargir ce flag-là au viewport ferait basculer tout le legacy dès qu'une fenêtre desktop passe
// sous le seuil — ce composable est donc un signal SÉPARÉ, réservé au Design System v2.
//
// `useMediaQuery` est importé explicitement : `@vueuse/nuxt` est dans les dépendances mais absent
// de `nuxt.config.ts:modules`, il n'y a donc pas d'auto-import vueuse dans ce projet.

export type DsBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

// Bornes basses des breakpoints Tailwind, alignées sur GD_BP_W côté amont.
export const DS_BREAKPOINT_WIDTHS: Record<DsBreakpoint, number> = {
  'sm': 640,
  'md': 768,
  'lg': 1024,
  'xl': 1280,
  '2xl': 1536,
};

// Formats qui montent l'arbre mobile (cf. isMobileBp amont).
const MOBILE_BREAKPOINTS: readonly DsBreakpoint[] = ['sm', 'md'];

interface DsBreakpointState {
  bp: ComputedRef<DsBreakpoint>;
  isMobileBp: ComputedRef<boolean>;
}

function createState (): DsBreakpointState {
  // Une media query par palier plutôt qu'un listener de resize : le navigateur ne réévalue que
  // les paliers réellement franchis, et la valeur est réactive sans code de nettoyage.
  const atLeastMd = useMediaQuery(`(min-width: ${DS_BREAKPOINT_WIDTHS.md}px)`);
  const atLeastLg = useMediaQuery(`(min-width: ${DS_BREAKPOINT_WIDTHS.lg}px)`);
  const atLeastXl = useMediaQuery(`(min-width: ${DS_BREAKPOINT_WIDTHS.xl}px)`);
  const atLeast2xl = useMediaQuery(`(min-width: ${DS_BREAKPOINT_WIDTHS['2xl']}px)`);

  const bp = computed<DsBreakpoint>(() => {
    if (atLeast2xl.value) { return '2xl'; }
    if (atLeastXl.value) { return 'xl'; }
    if (atLeastLg.value) { return 'lg'; }
    return atLeastMd.value ? 'md' : 'sm';
  });

  return { bp, isMobileBp: computed<boolean>(() => MOBILE_BREAKPOINTS.includes(bp.value)) };
}

// Format du viewport : une seule valeur pour toute l'app, dans un scope détaché. Le composable est
// appelé par le layout, plusieurs pages et des composants du chrome — sans partage, chacun
// enregistre ses 4 media queries, et un état créé dans le scope d'un composant meurt à son
// démontage alors que la fenêtre, elle, reste.
let shared: DsBreakpointState | null = null;

export function useDsBreakpoint (): DsBreakpointState {
  shared ??= effectScope(true).run(createState) as DsBreakpointState;
  return shared;
}
